import { Language } from '../types';

export const LANGUAGE_NAMES: Record<Language, string> = {
  en: 'English',
  fr: 'Français',
  de: 'Deutsch',
  es: 'Español',
  ja: '日本語',
  ko: '한국어',
  'zh-TW': '繁體中文',
};

export const translations = {
  en: {
    nav: {
      subtitle: "Precision Pour Tool",
      latest: "Latest Version",
    },
    hero: {
      title: "Create your masterpiece.",
      desc: "Calculate exact mixing ratios, estimate costs, and get AI-powered safety advice instantly.",
    },
    input: {
      title: "Project Specs",
      imperial: "Imperial",
      metric: "Metric",
      rectangle: "Rectangle",
      circle: "Circle",
      length: "Length",
      width: "Width",
      depth: "Depth",
      diameter: "Diameter",
      ratio: "Mixing Ratio (By Volume)",
      ratioPartA: "A",
      ratioPartB: "B",
      ratioExample: "Example: 1:1 or 2:1 (Resin : Hardener)",
      cost: "Resin Cost",
      pricePerGallon: "Price per Gallon",
      pricePerLiter: "Price per Liter",
      wasteMargin: "Safety/Waste Margin",
    },
    results: {
      title: "Total Volume Needed",
      estCost: "Est. Cost",
      resinA: "Resin (A)",
      hardenerB: "Hardener (B)",
      partA: "Part A",
      partB: "Part B",
      emptyState: "Enter dimensions to start",
    },
    advisor: {
      title: "AI Project Analysis",
      subtitle: "Safety, Cost & Technique Check",
      analyzeBtn: "Analyze Now",
      analyzingBtn: "Thinking...",
      loading: "Consulting resin database...",
      empty: "Calculate your volume first.",
      emptyDesc: "Our AI will review your specific dimensions to prevent overheating and wasted material.",
      chatTitle: "Resin Assistant",
      online: "ONLINE",
      chatEmpty: "Ask me anything about your project!",
      chatPlaceholder: "Ask about curing times, bubbles, or pigments...",
    },
    footer: {
      desc: "The most advanced tool for resin artists. Save money, mix accurately, and pour safely.",
      disclaimer: "Disclaimer: Calculations are estimates. Always perform a small test pour and follow manufacturer instructions.",
    },
  },
  fr: {
    nav: {
      subtitle: "Outil de Coulée de Précision",
      latest: "Dernière Version",
    },
    hero: {
      title: "Créez votre chef-d'œuvre.",
      desc: "Calculez les ratios de mélange exacts, estimez les coûts et obtenez des conseils de sécurité instantanés par IA.",
    },
    input: {
      title: "Spécifications",
      imperial: "Impérial",
      metric: "Métrique",
      rectangle: "Rectangle",
      circle: "Cercle",
      length: "Longueur",
      width: "Largeur",
      depth: "Profondeur",
      diameter: "Diamètre",
      ratio: "Ratio de Mélange (Volume)",
      ratioPartA: "A",
      ratioPartB: "B",
      ratioExample: "Exemple: 1:1 ou 2:1 (Résine : Durcisseur)",
      cost: "Coût de la Résine",
      pricePerGallon: "Prix par Gallon",
      pricePerLiter: "Prix par Litre",
      wasteMargin: "Marge de Sécurité",
    },
    results: {
      title: "Volume Total Requis",
      estCost: "Coût Est.",
      resinA: "Résine (A)",
      hardenerB: "Durcisseur (B)",
      partA: "Partie A",
      partB: "Partie B",
      emptyState: "Entrez les dimensions pour commencer",
    },
    advisor: {
      title: "Analyse IA du Projet",
      subtitle: "Sécurité, Coût et Technique",
      analyzeBtn: "Analyser",
      analyzingBtn: "Réflexion...",
      loading: "Consultation de la base de données...",
      empty: "Calculez d'abord votre volume.",
      emptyDesc: "Notre IA examinera vos dimensions pour éviter la surchauffe et le gaspillage.",
      chatTitle: "Assistant Résine",
      online: "EN LIGNE",
      chatEmpty: "Posez-moi n'importe quelle question sur votre projet !",
      chatPlaceholder: "Temps de durcissement, bulles, pigments...",
    },
    footer: {
      desc: "L'outil le plus avancé pour les artistes résine. Économisez, mélangez précisément et coulez en toute sécurité.",
      disclaimer: "Avis de non-responsabilité : Les calculs sont des estimations. Toujours faire un test et suivre les instructions du fabricant.",
    },
  },
  de: {
    nav: {
      subtitle: "Präzisions-Gießwerkzeug",
      latest: "Neueste Version",
    },
    hero: {
      title: "Erschaffe dein Meisterwerk.",
      desc: "Berechne exakte Mischverhältnisse, schätze Kosten und erhalte sofort KI-gestützte Sicherheitstipps.",
    },
    input: {
      title: "Projektdaten",
      imperial: "Imperial",
      metric: "Metrisch",
      rectangle: "Rechteck",
      circle: "Kreis",
      length: "Länge",
      width: "Breite",
      depth: "Tiefe",
      diameter: "Durchmesser",
      ratio: "Mischverhältnis (Volumen)",
      ratioPartA: "A",
      ratioPartB: "B",
      ratioExample: "Beispiel: 1:1 oder 2:1 (Harz : Härter)",
      cost: "Harzkosten",
      pricePerGallon: "Preis pro Gallone",
      pricePerLiter: "Preis pro Liter",
      wasteMargin: "Sicherheitsmarge",
    },
    results: {
      title: "Benötigtes Gesamtvolumen",
      estCost: "Gesch. Kosten",
      resinA: "Harz (A)",
      hardenerB: "Härter (B)",
      partA: "Teil A",
      partB: "Teil B",
      emptyState: "Maße eingeben zum Starten",
    },
    advisor: {
      title: "KI-Projektanalyse",
      subtitle: "Sicherheit, Kosten & Technik",
      analyzeBtn: "Jetzt Analysieren",
      analyzingBtn: "Denke nach...",
      loading: "Konsultiere Harz-Datenbank...",
      empty: "Berechne zuerst dein Volumen.",
      emptyDesc: "Unsere KI prüft deine Maße, um Überhitzung und Materialverschwendung zu vermeiden.",
      chatTitle: "Harz-Assistent",
      online: "ONLINE",
      chatEmpty: "Frag mich alles über dein Projekt!",
      chatPlaceholder: "Fragen zu Aushärtung, Blasen oder Pigmenten...",
    },
    footer: {
      desc: "Das fortschrittlichste Tool für Harzkünstler. Spare Geld, mische genau und arbeite sicher.",
      disclaimer: "Haftungsausschluss: Berechnungen sind Schätzungen. Immer einen kleinen Test durchführen und Herstelleranweisungen befolgen.",
    },
  },
  es: {
    nav: {
      subtitle: "Herramienta de Precisión",
      latest: "Última Versión",
    },
    hero: {
      title: "Crea tu obra maestra.",
      desc: "Calcula proporciones exactas, estima costos y recibe consejos de seguridad de IA al instante.",
    },
    input: {
      title: "Especificaciones",
      imperial: "Imperial",
      metric: "Métrico",
      rectangle: "Rectángulo",
      circle: "Círculo",
      length: "Largo",
      width: "Ancho",
      depth: "Profundidad",
      diameter: "Diámetro",
      ratio: "Proporción (Volumen)",
      ratioPartA: "A",
      ratioPartB: "B",
      ratioExample: "Ejemplo: 1:1 o 2:1 (Resina : Endurecedor)",
      cost: "Costo de Resina",
      pricePerGallon: "Precio por Galón",
      pricePerLiter: "Precio por Litro",
      wasteMargin: "Margen de Seguridad",
    },
    results: {
      title: "Volumen Total Necesario",
      estCost: "Costo Est.",
      resinA: "Resina (A)",
      hardenerB: "Endurecedor (B)",
      partA: "Parte A",
      partB: "Parte B",
      emptyState: "Ingresa dimensiones para iniciar",
    },
    advisor: {
      title: "Análisis de Proyecto IA",
      subtitle: "Seguridad, Costo y Técnica",
      analyzeBtn: "Analizar Ahora",
      analyzingBtn: "Pensando...",
      loading: "Consultando base de datos...",
      empty: "Calcula tu volumen primero.",
      emptyDesc: "Nuestra IA revisará tus dimensiones para prevenir sobrecalentamiento y desperdicio.",
      chatTitle: "Asistente de Resina",
      online: "EN LÍNEA",
      chatEmpty: "¡Pregúntame cualquier cosa sobre tu proyecto!",
      chatPlaceholder: "Tiempos de curado, burbujas o pigmentos...",
    },
    footer: {
      desc: "La herramienta más avanzada para artistas de resina. Ahorra dinero, mezcla con precisión y trabaja seguro.",
      disclaimer: "Descargo: Los cálculos son estimaciones. Siempre realiza una prueba pequeña y sigue las instrucciones del fabricante.",
    },
  },
  ja: {
    nav: {
      subtitle: "高精度レジン計算ツール",
      latest: "最新バージョン",
    },
    hero: {
      title: "最高傑作を作ろう。",
      desc: "正確な混合比率を計算し、コストを見積もり、AIによる安全アドバイスを即座に取得します。",
    },
    input: {
      title: "プロジェクト仕様",
      imperial: "インチ(Imperial)",
      metric: "メートル(Metric)",
      rectangle: "長方形",
      circle: "円形",
      length: "長さ",
      width: "幅",
      depth: "深さ",
      diameter: "直径",
      ratio: "混合比率 (体積)",
      ratioPartA: "A",
      ratioPartB: "B",
      ratioExample: "例: 1:1 または 2:1 (主剤 : 硬化剤)",
      cost: "レジン費用",
      pricePerGallon: "ガロンあたりの価格",
      pricePerLiter: "リットルあたりの価格",
      wasteMargin: "安全・廃棄マージン",
    },
    results: {
      title: "必要総量",
      estCost: "推定費用",
      resinA: "主剤 (A)",
      hardenerB: "硬化剤 (B)",
      partA: "パート A",
      partB: "パート B",
      emptyState: "寸法を入力して開始",
    },
    advisor: {
      title: "AIプロジェクト分析",
      subtitle: "安全性、コスト、技術チェック",
      analyzeBtn: "今すぐ分析",
      analyzingBtn: "思考中...",
      loading: "レジンデータベースを参照中...",
      empty: "最初に体積を計算してください。",
      emptyDesc: "AIが寸法を確認し、過熱や材料の無駄を防ぎます。",
      chatTitle: "レジンアシスタント",
      online: "オンライン",
      chatEmpty: "プロジェクトについて何でも聞いてください！",
      chatPlaceholder: "硬化時間、気泡、顔料について...",
    },
    footer: {
      desc: "レジンアーティストのための最先端ツール。コストを節約し、正確に混合し、安全に作業しましょう。",
      disclaimer: "免責事項：計算は概算です。常に少量のテストを行い、メーカーの指示に従ってください。",
    },
  },
  ko: {
    nav: {
      subtitle: "정밀 레진 계산 도구",
      latest: "최신 버전",
    },
    hero: {
      title: "당신만의 걸작을 만드세요.",
      desc: "정확한 혼합 비율을 계산하고, 비용을 견적하며, AI의 안전 조언을 즉시 받으세요.",
    },
    input: {
      title: "프로젝트 사양",
      imperial: "임페리얼 (인치)",
      metric: "메트릭 (cm)",
      rectangle: "직사각형",
      circle: "원형",
      length: "길이",
      width: "너비",
      depth: "깊이",
      diameter: "지름",
      ratio: "혼합 비율 (부피)",
      ratioPartA: "A",
      ratioPartB: "B",
      ratioExample: "예: 1:1 또는 2:1 (주제 : 경화제)",
      cost: "레진 비용",
      pricePerGallon: "갤런당 가격",
      pricePerLiter: "리터당 가격",
      wasteMargin: "안전/여유분",
    },
    results: {
      title: "필요 총량",
      estCost: "예상 비용",
      resinA: "주제 (A)",
      hardenerB: "경화제 (B)",
      partA: "파트 A",
      partB: "파트 B",
      emptyState: "치수를 입력하여 시작",
    },
    advisor: {
      title: "AI 프로젝트 분석",
      subtitle: "안전, 비용 및 기술 점검",
      analyzeBtn: "지금 분석",
      analyzingBtn: "생각 중...",
      loading: "레진 데이터베이스 조회 중...",
      empty: "먼저 부피를 계산하세요.",
      emptyDesc: "AI가 치수를 검토하여 과열과 재료 낭비를 방지합니다.",
      chatTitle: "레진 어시스턴트",
      online: "온라인",
      chatEmpty: "프로젝트에 대해 무엇이든 물어보세요!",
      chatPlaceholder: "경화 시간, 기포, 안료 등에 대해 질문하세요...",
    },
    footer: {
      desc: "레진 아티스트를 위한 최첨단 도구입니다. 비용을 절약하고, 정확하게 혼합하며, 안전하게 작업하세요.",
      disclaimer: "면책 조항: 계산은 추정치입니다. 항상 소량 테스트를 수행하고 제조사의 지침을 따르십시오.",
    },
  },
  'zh-TW': {
    nav: {
      subtitle: "精準環氧樹脂計算工具",
      latest: "最新版本",
    },
    hero: {
      title: "創造你的傑作。",
      desc: "計算精確的混合比例，估算成本，並即時獲得 AI 的安全建議。",
    },
    input: {
      title: "項目規格",
      imperial: "英制 (Imperial)",
      metric: "公制 (Metric)",
      rectangle: "長方形",
      circle: "圓形",
      length: "長度",
      width: "寬度",
      depth: "深度",
      diameter: "直徑",
      ratio: "混合比例 (體積)",
      ratioPartA: "A",
      ratioPartB: "B",
      ratioExample: "例如：1:1 或 2:1 (樹脂 : 硬化劑)",
      cost: "樹脂成本",
      pricePerGallon: "每加侖價格",
      pricePerLiter: "每公升價格",
      wasteMargin: "安全/損耗預留",
    },
    results: {
      title: "所需總體積",
      estCost: "預估成本",
      resinA: "樹脂 (A)",
      hardenerB: "硬化劑 (B)",
      partA: "A 劑",
      partB: "B 劑",
      emptyState: "輸入尺寸以開始",
    },
    advisor: {
      title: "AI 項目分析",
      subtitle: "安全、成本與技術檢查",
      analyzeBtn: "立即分析",
      analyzingBtn: "思考中...",
      loading: "正在查詢樹脂數據庫...",
      empty: "請先計算體積。",
      emptyDesc: "我們的 AI 將審查您的具體尺寸，以防止過熱和材料浪費。",
      chatTitle: "樹脂助手",
      online: "在線",
      chatEmpty: "關於您的項目，儘管問我！",
      chatPlaceholder: "詢問固化時間、氣泡或色漿...",
    },
    footer: {
      desc: "最先進的樹脂藝術家工具。節省金錢，精確混合，安全澆注。",
      disclaimer: "免責聲明：計算結果僅供參考。請務必進行小量測試並遵循製造商的說明。",
    },
  },
};